import bindAll from 'lodash.bindall';
import PropTypes from 'prop-types';
import React from 'react';
import {connect} from 'react-redux';
import classNames from 'classnames';

import {searchBlocks, getExtensionBlocks} from '../../lib/block-definitions.js';
import styles from './block-search-bar.css';

// 検索アイコン
const SearchIcon = () => (
    <svg
        className={styles.searchIcon}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <circle
            cx="11"
            cy="11"
            r="8"
        />
        <path d="M21 21l-4.35-4.35" />
    </svg>
);

// クリアアイコン
const ClearIcon = () => (
    <svg
        className={styles.clearIcon}
        viewBox="0 0 24 24"
        fill="currentColor"
    >
        {/* eslint-disable-next-line max-len */}
        <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
    </svg>
);

class BlockSearchBar extends React.Component {
    constructor (props) {
        super(props);
        bindAll(this, [
            'handleChange',
            'handleClear',
            'handleFocus',
            'handleBlur',
            'handleKeyDown',
            'handleResultClick',
            'handleResultMouseDown',
            'handleGlobalKeyDown',
            'setInputRef',
            'getWorkspace',
            'scrollToCategory',
            'highlightBlockInFlyout',
            'applyHighlightEffect'
        ]);

        this.state = {
            query: '',
            results: [],
            selectedIndex: 0,
            isFocused: false
        };

        this.inputRef = null;
        this.blurTimeout = null;
        this.isScrolling = false;
    }

    componentDidMount () {
        // Ctrl+F でフォーカス
        document.addEventListener('keydown', this.handleGlobalKeyDown);
    }

    componentWillUnmount () {
        document.removeEventListener('keydown', this.handleGlobalKeyDown);
        if (this.blurTimeout) {
            clearTimeout(this.blurTimeout);
        }
    }

    setInputRef (element) {
        this.inputRef = element;
    }

    handleGlobalKeyDown (e) {
        if ((e.ctrlKey || e.metaKey) && e.key === 'f') {
            // コードタブが表示されている場合のみ
            if (this.props.isBlocksTabVisible) {
                e.preventDefault();
                if (this.inputRef) {
                    this.inputRef.focus();
                    this.inputRef.select();
                }
            }
        }
    }

    getAllSearchableBlocks (query) {
        const {vm, locale} = this.props;
        
        // 標準ブロックの検索結果
        let results = searchBlocks(query, locale);
        
        // 拡張機能のブロックを追加
        if (vm && vm.runtime && vm.runtime._blockInfo) {
            const extensionBlocks = getExtensionBlocks(vm.runtime._blockInfo, query);
            results = results.concat(extensionBlocks);
        }
        
        // 最大20件に制限
        return results.slice(0, 20);
    }

    handleChange (e) {
        const query = e.target.value;
        const results = query ? this.getAllSearchableBlocks(query) : [];

        this.setState({
            query,
            results,
            selectedIndex: 0,
            isFocused: true // 入力中は常にフォーカス状態
        });
    }

    handleClear () {
        this.setState({
            query: '',
            results: [],
            selectedIndex: 0
        });
        if (this.inputRef) {
            this.inputRef.focus();
        }
    }

    handleFocus () {
        if (this.blurTimeout) {
            clearTimeout(this.blurTimeout);
        }
        this.setState({isFocused: true});
    }

    handleBlur () {
        // 結果クリック時にblurが先に発火するのを防ぐため、少し遅延
        this.blurTimeout = setTimeout(() => {
            this.setState({isFocused: false});
        }, 150);
    }

    handleKeyDown (e) {
        const {results, selectedIndex} = this.state;

        switch (e.key) {
        case 'ArrowDown':
            e.preventDefault();
            if (results.length > 0) {
                this.setState({
                    selectedIndex: (selectedIndex + 1) % results.length
                });
            }
            break;

        case 'ArrowUp':
            e.preventDefault();
            if (results.length > 0) {
                this.setState({
                    selectedIndex: selectedIndex <= 0 ? results.length - 1 : selectedIndex - 1
                });
            }
            break;

        case 'Enter':
            e.preventDefault();
            if (results.length > 0 && results[selectedIndex]) {
                this.handleResultClick(results[selectedIndex]);
            }
            break;

        case 'Escape':
            e.preventDefault();
            this.setState({query: '', results: [], isFocused: false});
            if (this.inputRef) {
                this.inputRef.blur();
            }
            break;
        }
    }

    handleResultMouseDown (e) {
        // blurイベントより先に処理するため
        e.preventDefault();
    }

    handleResultClick (block) {
        this.scrollToCategory(block.category, block.type);
        this.setState({
            query: '',
            results: [],
            isFocused: false
        });
    }

    getWorkspace () {
        // scratch-blocksからメインワークスペースを取得
        try {
            const ScratchBlocks = require('scratch-blocks');
            return ScratchBlocks.getMainWorkspace();
        } catch (err) {
            console.warn('BlockSearchBar: Could not get workspace', err);
            return null;
        }
    }

    scrollToCategory (categoryId, blockType) {
        // 処理中なら無視
        if (this.isScrolling) {
            return;
        }
        
        const workspace = this.getWorkspace();
        if (!workspace) {
            return;
        }

        try {
            this.isScrolling = true;

            const flyout = workspace.getFlyout();
            if (!flyout) {
                this.isScrolling = false;
                return;
            }

            const flyoutWorkspace = flyout.getWorkspace();
            if (!flyoutWorkspace) {
                this.isScrolling = false;
                return;
            }

            const allBlocks = flyoutWorkspace.getAllBlocks();
            const targetBlock = allBlocks.find(block => block.type === blockType);

            if (targetBlock && flyout.scrollbar_) {
                const blockSvg = targetBlock.getSvgRoot();
                const targetBlockY = targetBlock.getRelativeToSurfaceXY().y;
                const flyoutHeight = flyout.getHeight ? flyout.getHeight() : flyout.height_ || 400;
                
                // Flyoutのワークスペースからscaleとmetricsを取得
                const scale = flyoutWorkspace.scale || 1;
                const metrics = flyoutWorkspace.getMetrics ? flyoutWorkspace.getMetrics() : null;
                const contentTop = metrics ? metrics.contentTop : 0;
                
                // スケーリングを考慮したスクロール位置を計算
                const scaledY = (targetBlockY - contentTop) * scale;
                const scrollTarget = Math.max(0, scaledY - (flyoutHeight / 2) + 40);

                // スクロール実行
                flyout.scrollbar_.set(scrollTarget);

                // ハイライト
                setTimeout(() => {
                    if (blockSvg) {
                        this.applyHighlightEffect(blockSvg);
                    }
                    this.isScrolling = false;
                }, 100);
            } else {
                this.isScrolling = false;
            }
        } catch (err) {
            this.isScrolling = false;
        }
    }

    highlightBlockInFlyout () {
        // 互換性のために残す（使用されていない）
    }

    applyHighlightEffect (blockSvg) {
        const originalFilter = blockSvg.style.filter;
        let count = 0;
        const maxBlinks = 6;

        const blink = () => {
            if (count >= maxBlinks) {
                blockSvg.style.filter = originalFilter;
                return;
            }

            if (count % 2 === 0) {
                blockSvg.style.filter = 'brightness(1.3) drop-shadow(0 0 8px rgba(255, 255, 255, 0.8))';
            } else {
                blockSvg.style.filter = originalFilter;
            }

            count++;
            setTimeout(blink, 150);
        };

        blink();
    }

    render () {
        const {query, results, selectedIndex, isFocused} = this.state;
        const {locale} = this.props;
        const lang = locale && locale.startsWith('ja') ? 'ja' : 'en';
        const showResults = isFocused && results.length > 0;

        return (
            <div className={styles.searchBarContainer}>
                <div
                    className={classNames(styles.searchInputWrapper, {
                        [styles.focused]: isFocused
                    })}
                >
                    <SearchIcon />
                    <input
                        ref={this.setInputRef}
                        className={styles.searchInput}
                        type="text"
                        value={query}
                        onChange={this.handleChange}
                        onFocus={this.handleFocus}
                        onBlur={this.handleBlur}
                        onKeyDown={this.handleKeyDown}
                        placeholder={lang === 'ja' ? 'ブロック検索' : 'Search blocks'}
                    />
                    {query && (
                        <button
                            className={styles.clearButton}
                            onClick={this.handleClear}
                            tabIndex={-1}
                        >
                            <ClearIcon />
                        </button>
                    )}
                </div>

                {showResults && (
                    <div className={styles.resultsDropdown}>
                        {results.map((block, index) => (
                            <button
                                key={`${block.type}-${index}`}
                                className={classNames(styles.resultItem, {
                                    [styles.selected]: index === selectedIndex
                                })}
                                onMouseDown={this.handleResultMouseDown}
                                // eslint-disable-next-line react/jsx-no-bind
                                onClick={() => this.handleResultClick(block)}
                            >
                                <span
                                    className={styles.categoryDot}
                                    style={{
                                        backgroundColor: block.categoryInfo?.color || '#888'
                                    }}
                                />
                                <span className={styles.blockName}>
                                    {block.displayName}
                                </span>
                                <span className={styles.categoryName}>
                                    {block.categoryInfo?.name[lang] || block.category}
                                </span>
                            </button>
                        ))}
                    </div>
                )}
            </div>
        );
    }
}

BlockSearchBar.propTypes = {
    vm: PropTypes.object,
    locale: PropTypes.string,
    isBlocksTabVisible: PropTypes.bool
};

const mapStateToProps = state => ({
    locale: state.locales.locale,
    vm: state.scratchGui.vm,
    isBlocksTabVisible: state.scratchGui.editorTab.activeTabIndex === 0
});

export default connect(mapStateToProps)(BlockSearchBar);
